/**
 * Phase 2G.8 - daily operations & shift handover accountability.
 *
 * An operational handover documents UNFINISHED WORK moving between
 * people: which transactions are still in progress, what the last
 * completed step was, what remains open, and who prepared/accepted the
 * handover for one branch and one Asia/Manila business day.
 *
 * Honest boundaries (stated, not hidden):
 *   - the system records NO shifts, schedules or clock-in/out times.
 *     This is an operational handover (preparing and accepting actors
 *     with server timestamps), not attendance or shift management.
 *   - the snapshot is taken server-side at submission from real stored
 *     data (status, processing steps, counts, exception categories) and
 *     is immutable afterwards; the CURRENT state of a transaction is a
 *     separate, live view - the two are compared, never conflated.
 *   - no customer personal information is copied into handover records.
 *   - "unresolved" is a factual predicate (open processing stage or an
 *     item-count discrepancy recorded) - never blame, never a score.
 */

/** Handover lifecycle: DRAFT -> SUBMITTED -> ACCEPTED (forward only). */
export const HANDOVER_STATUSES = ['DRAFT', 'SUBMITTED', 'ACCEPTED'] as const;
export type HandoverStatus = (typeof HANDOVER_STATUSES)[number];

export const HANDOVER_STATUS_LABELS: Record<HandoverStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  ACCEPTED: 'Accepted',
};

/**
 * Transaction statuses considered ACTIVE operational work - the set a
 * handover snapshot covers (RELEASED/DONE are finished business and are
 * deliberately excluded; the workflows themselves are untouched).
 */
export const ACTIVE_TRANSACTION_STATUSES = [
  'PENDING',
  'WASHING',
  'FOLDING',
  'READY',
] as const;

/** True when a transaction status is unfinished work a handover carries. */
export function isActiveWorkStatus(status: string): boolean {
  return (ACTIVE_TRANSACTION_STATUSES as readonly string[]).includes(status);
}

/** Factual per-transaction snapshot predicates shown in handover views. */
export const HANDOVER_SNAPSHOT_FLAGS = ['OPEN_STAGE', 'ITEM_DISCREPANCY'] as const;
export type HandoverSnapshotFlag = (typeof HANDOVER_SNAPSHOT_FLAGS)[number];

export const HANDOVER_SNAPSHOT_FLAG_LABELS: Record<HandoverSnapshotFlag, string> = {
  OPEN_STAGE: 'Open processing stage',
  ITEM_DISCREPANCY: 'Item-count discrepancy',
};

export const HANDOVER_SNAPSHOT_FLAG_HINTS: Record<HandoverSnapshotFlag, string> = {
  OPEN_STAGE: 'A processing step was started but not completed when the snapshot was taken',
  ITEM_DISCREPANCY: 'The latest completed step recorded an actual count different from the expected count',
};
